import React, { useMemo, useState } from 'react';
import apiClient from '../lib/apiClient';
import useRecaptcha from '../hooks/useRecaptcha';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { containerRef: recaptchaRef, execute: runRecaptcha, ready: recaptchaReady, error: recaptchaError } = useRecaptcha(
        'login',
        { size: 'normal' }
    );
    const verificationNotice = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('verifyEmail') !== '1') {
            return '';
        }
        const emailParam = params.get('email');
        return emailParam
            ? `Signup successful. We sent a verification link to ${emailParam}. Please verify your email before signing in.`
            : 'Signup successful. Please check your email for the verification link before signing in.';
    }, []);

    const handleGoogleLogin = () => {
        window.location.href = '/auth/google';
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (recaptchaError) {
                throw new Error(recaptchaError.message || 'reCAPTCHA failed to load. Please refresh and retry.');
            }

            if (!recaptchaReady) {
                throw new Error('Security check is still initializing. Please wait a moment.');
            }

            const token = await runRecaptcha();

            await apiClient.post('/auth/login', { email, password, token, 'g-recaptcha-response': token });
            const params = new URLSearchParams(window.location.search);
            const redirect = params.get('redirect') || '/dashboard';
            window.location.href = redirect;
        } catch (err) {
            setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #3457D5 0%, #F28C46 100%)',
            padding: '20px',
        },
        card: {
            background: '#FFFFFF',
            borderRadius: '24px',
            padding: '48px',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        },
        logo: {
            display: 'block',
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            margin: '0 auto 12px',
        },
        logoText: {
            fontSize: '32px',
            fontWeight: '800',
            color: '#3457D5',
            marginBottom: '12px',
            textAlign: 'center',
        },
        title: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#2B2B2B',
            marginBottom: '8px',
            textAlign: 'center',
        },
        subtitle: {
            fontSize: '15px',
            color: '#6E6E6E',
            marginBottom: '32px',
            textAlign: 'center',
        },
        googleButton: {
            width: '100%',
            padding: '16px',
            background: '#FFFFFF',
            border: '2px solid #E5E5E5',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#414141',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'all 0.3s',
        },
        googleIcon: {
            width: '24px',
            height: '24px',
        },
        footer: {
            marginTop: '24px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#6E6E6E',
        },
        link: {
            color: '#3457D5',
            textDecoration: 'none',
            fontWeight: '600',
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            fontSize: '15px',
            border: '1px solid #E5E5E5',
            borderRadius: '12px',
            marginBottom: '16px',
            outline: 'none',
        },
        loginButton: {
            width: '100%',
            padding: '16px',
            background: '#3457D5',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s',
            marginBottom: '24px',
        },
        divider: {
            display: 'flex',
            alignItems: 'center',
            textAlign: 'center',
            margin: '24px 0',
            color: '#6E6E6E',
            fontSize: '14px',
        },
        dividerLine: {
            flex: 1,
            borderBottom: '1px solid #E5E5E5',
        },
        error: {
            background: '#FFE0E0',
            color: '#D81F26',
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px',
        },
        success: {
            background: '#E7F7EC',
            color: '#1E7A3E',
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px',
        },
        recaptchaWrapper: {
            margin: '12px 0',
            display: 'flex',
            justifyContent: 'center',
        },
        recaptchaNote: {
            fontSize: '11px',
            color: '#6E6E6E',
            textAlign: 'center',
            marginBottom: '20px',
        },
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <img src="/corpusio-logo.webp" alt="Corpusio" style={styles.logo} />
                <div style={styles.logoText}>Corpusio</div>
                <h1 style={styles.title}>Welcome Back</h1>
                <p style={styles.subtitle}>
                    Sign in to access your dashboard and manage your AI solutions
                </p>


                {error && <div style={styles.error}>{error}</div>}
                {verificationNotice && !error && <div style={styles.success}>{verificationNotice}</div>}
                {recaptchaError && !error && (
                    <div style={styles.error}>
                        {recaptchaError.message || 'Unable to load reCAPTCHA. Refresh and try again.'}
                    </div>
                )}

                <form onSubmit={handleEmailLogin}>
                    <input
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={styles.input}
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="Password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={styles.input}
                    />
                    <div style={{ textAlign: 'right', marginTop: '-6px', marginBottom: '14px' }}>
                        <a href="/forgot-password" style={styles.link}>Forgot your password?</a>
                    </div>
                    <button
                        type="submit"
                        style={styles.loginButton}
                        disabled={loading}
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>
                <div style={styles.recaptchaWrapper}>
                    <div ref={recaptchaRef} />
                </div>
                <p style={styles.recaptchaNote}>
                    Protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
                    <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                </p>

                <div style={styles.divider}>
                    <div style={styles.dividerLine}></div>
                    <span style={{ padding: '0 10px' }}>OR</span>
                    <div style={styles.dividerLine}></div>
                </div>
                <button
                    style={styles.googleButton}
                    onClick={handleGoogleLogin}
                    onMouseEnter={(e) => {
                        e.target.style.border = '2px solid #3457D5';
                        e.target.style.boxShadow = '0 4px 12px rgba(52,87,213,0.2)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.border = '2px solid #E5E5E5';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                    <svg style={styles.googleIcon} viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                </button>

                <div style={styles.footer}>
                    Don't have an account?{' '}
                    <a href="/signup" style={styles.link}>Sign up</a>
                </div>
            </div>
        </div>
    );
};

export default Login;
